/* oxlint-disable max-lines */
import { IChatV2Schema } from '@cian/chats-api';

import { UnknownError } from '@cian/peperrors';

import { openEventChannel } from './openEventChannel';
import { IWsChatHintsEvent } from './types';
import { TFrameCompanion } from '../..//types/openedChats';
import { BLOB_ID } from '../../constants/blobId';
import { trackFileSend } from '../../containers/AttachFileButtonContainer/tracking';
import { trackUnarchivedButtonClick } from '../../containers/MessageInputFieldContainer/tracking';
import { resetSavedDeletedMessageId } from '../../slices/deleteMessageSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';
import { EHostType } from '../../types/hostType';
import { TFileTypes } from '../../types/messageFiles';
import { EMobileLayoutActiveSide } from '../../types/mobileSide';
import { TThunkAction } from '../../types/redux/thunk';
import { ERoute } from '../../types/route';
import { ETelemetryEvents } from '../../types/telemetry';
import { deepEqual } from '../../utils/deepEqual';
import { mapMessage } from '../../mappers';
import { addIncomingMessage } from '../chat/actions';
import { setHintQuestionsVisible } from '../chatHintQuestions';
import { updateChatImages } from '../chatImages';
import { toggleChangeListActive, updatedFromServer } from '../chatList';
import { removeFromArchive } from '../chatOperations';
import { openChat, closeChat } from '../chatWindow';
import { setWsSendSucceed } from '../files/actions';
import { setMobileActiveSide } from '../mobileSide';
import { addOpenedChat, deleteOpenedChat } from '../openedChats/actions';
import { changeRoute } from '../route';
import { setSendFirstMessageSuccess, setSendMessageSuccess, markMessageAsDeleted } from '../sendMessage';
import { setUnreadMessagesCount } from '../unreadMessagesCount';
import { closeUserCard } from '../userCard/closeUserCard';
import { setUserStatus } from '../userStatus';
import { setWsError } from '../wsError';
import { setWsModalError } from '../wsError/setWsModalError';
import { IWsErrorEvent, IWsModalErrorEvent } from '../wsError/types';

export const listenEventChannel =
  (): TThunkAction<void> =>
  (dispatch, getState, { logger, telemetry, custom: { messagesMap, socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const onMessageId = eventChannel.addIncommingMessageListener(({ data }) => {
      const state = getState();

      const {
        chatList,
        chatWindow,
        hostType,
        user,
        openedChats,
        deleteMessage: { deletedMessageId },
      } = state;
      const { isChangeListActive, chats, currentRoute } = chatList;

      // Обработка нового события chatHints (дебильнички по сокету)
      // Вынесено из switch т.к. тип события не определен в библиотеке сокетов
      if ((data as unknown as IWsChatHintsEvent).type === 'chatHints') {
        const { chatId: hintsChatId, hints: chatHints } = data as unknown as IWsChatHintsEvent;

        if (hintsChatId && chatHints?.length && openedChats[hintsChatId]) {
          dispatch(setHintQuestionsVisible({ chatId: hintsChatId, hints: chatHints }));
        }

        return;
      }

      // tslint:disable-next-line:switch-default
      switch (data.type) {
        case 'chat': {
          // После добавления custom actions нужно дополнительно проверять, что изменился статус чата (заблокирован/удален/заархивирован)
          const currentChat = chats.find(chat => chat.chatId === data.chat.chatId);

          const chat = data.chat as unknown as IChatV2Schema;

          // TODO уберем когда и ручки чатов обновятся
          const isArchived = chat.status === 'archived';
          const isBlocked = chat.status === 'blocked';
          const isDeleted = chat.status === 'deleted';

          const wasChangedChatCustomActions = currentChat
            ? // oxlint-disable-next-line typescript/no-explicit-any
              !deepEqual(currentChat.customActions as any, chat.customActions as any)
            : false;

          const wasChangedChatStatus =
            currentChat &&
            ((currentChat.status === 'archived') !== isArchived ||
              (currentChat.status === 'blocked') !== isBlocked ||
              chat.status === 'deleted');

          dispatch(updatedFromServer(chat));

          // Если чатов нет, а изменение списка активно, то деактивировать изменение списка
          if (!chats.length && isChangeListActive) {
            dispatch(toggleChangeListActive());
          }
          if (wasChangedChatCustomActions && !wasChangedChatStatus && currentRoute === ERoute.Archive) {
            return;
          }

          // Если чат был открыт и его удалили или перенесли в архив
          if (chatWindow.currentOpenedChatId === chat.chatId && (isDeleted || isArchived)) {
            dispatch(closeUserCard());

            // Если чат не в iFrame, то закрыть чат и переключиться на список чатов
            if (hostType !== EHostType.Frame) {
              dispatch(closeChat());
              dispatch(setMobileActiveSide(EMobileLayoutActiveSide.Left));
            } else {
              // Если чат в iFrame, то переключить пользователя на BLOB_ID и удалить чат
              const agentUser = chat.users.find(user => user.chatRole === 'opponent');

              const {
                offer,
                agentId,
                frameCompanion,
                users,
                editableMessage,
                defaultMessagePayload,
                sendDefaultMessage,
                hintQuestions: { hints, banner },
                ga: { source, products },
                availableDates: { checkin, checkout },
                header,
              } = openedChats[chatWindow.currentOpenedChatId];
              dispatch(
                addOpenedChat({
                  chatId: BLOB_ID,
                  offer,
                  header,
                  newChat: {
                    agentId,
                    users: users ?? chat.users,
                    // TODO нужно исправить на бекенде для версии v2
                    frameCompanion: frameCompanion || {
                      ...agentUser,
                      userId: agentUser?.userId || 0,
                      name: agentUser?.name || '',
                      chatRole: agentUser?.chatRole as unknown as TFrameCompanion['chatRole'],
                      agentAccountType: agentUser?.agentAccountType as unknown as TFrameCompanion['agentAccountType'],
                      hasPublicProfile: agentUser?.hasPublicProfile || false,
                      urlToAgentPublicProfile: '',
                      isAvailableForPhoneCall: agentUser?.isAvailableForPhoneCall || false,
                    },
                    defaultMessage: editableMessage,
                    defaultMessagePayload,
                    sendDefaultMessage,
                    hintQuestions: hints,
                    hintQuestionBanner: banner,
                    source,
                    gaProducts: products,
                    checkin,
                    checkout,
                    status: ERequestApiStatus.Succeed,
                  },
                }),
              );
              dispatch(openChat(BLOB_ID));
              dispatch(deleteOpenedChat(chatWindow.currentOpenedChatId));
            }

            return;
          }

          if (currentRoute === ERoute.Archive && chatWindow.currentOpenedChatId === chat.chatId) {
            dispatch(changeRoute({ route: ERoute.Messages, notClearCurrentOpenedChatId: true }));
          }

          if (!chat.lastMessage?.uid) {
            return;
          }

          const event = clientEvents.get(chat.lastMessage.uid);

          /**
           * При отсылке сообщения c веб от сервера приходит 2 сообщения: chat и message.
           * В случае, если это исходящее сообщение, сообщение chat нужно игнорировать,
           * так как статус выставляет сообщение message.
           * Однако, стоит помнить, что с сервера приходят сообщения от других участников и
           * их надо добавлять, как входящие. Также статус сообщения может быть изменен на Прочитано
           */
          if (!event || (event.type === EWsClientEventType.Message && event.status !== ERequestApiStatus.Pending)) {
            // если сообщение удалено, то не обрабатываем его
            if (deletedMessageId === chat.lastMessage.messageId) {
              return;
            }

            telemetry.increment(ETelemetryEvents.ChatWsMessageGet);

            dispatch(addIncomingMessage({ message: mapMessage(chat.lastMessage), messagesMap }));

            // Если пришло сообщение с фото - обновить последние изображения чата
            if (chat.lastMessage.files?.images?.length) {
              dispatch(updateChatImages({ chatId: chat.chatId as string }));
            }
          }

          break;
        }
        case 'message': {
          const { uid } = data;
          const message = mapMessage(data.message);
          const { files, chatId, type, userId: messageUserId, messageId, status } = message;

          // Удалённое сообщение — эхо инициатору или событие собеседнику (входящее/исходящее)
          if (status === 'deleted') {
            dispatch(
              markMessageAsDeleted({
                message,
                messagesMap,
                shouldDecreaseUnread: messageUserId !== user?.userId,
              }),
            );

            if (deletedMessageId === messageId) {
              dispatch(resetSavedDeletedMessageId());
            }

            return;
          }

          const { images = [], documents = [], imagesAsDocuments = [], fileTypes = [] } = files || {};
          const messageChat = chats.find(chat => chat.chatId === chatId);

          const currentOpenedChat = chatWindow.currentOpenedChatId === BLOB_ID ? openedChats[BLOB_ID] : undefined;

          const isNewChat =
            chatId &&
            user?.userId === messageUserId &&
            currentOpenedChat &&
            currentOpenedChat.firstMessageStatus === EFirstMessageStatus.Sent &&
            currentOpenedChat.newChatId === chatId;

          const opponent = messageChat?.users?.find(user => user.chatRole === 'opponent');

          // При отправке первого исходящего сообщения по ручке обратно приходит вебсокет с этим сообщением
          if (isNewChat) {
            telemetry.increment(ETelemetryEvents.ChatWsFirstMessageGetSuccess);
            dispatch(setSendFirstMessageSuccess({ message, messagesMap }));
            dispatch(openChat(message.chatId));
            dispatch(deleteOpenedChat(BLOB_ID));

            return;
          }

          // Вебсокет message без data.uid приходит всегда, когда появляется новое сообщение.
          // Вебсокет message c data.uid приходит только отправителю, когда он отправил сообщение через WebSocket с этим uid.
          const clientEvent = uid ? clientEvents.get(uid) : undefined;

          if (!uid || !clientEvent || clientEvent.type !== EWsClientEventType.Message) {
            return;
          }

          /**
           * @description
           * Трекаем только те сообщения, которые пришли в статусе Pending
           */
          if (clientEvent.status === ERequestApiStatus.Pending) {
            telemetry.increment(ETelemetryEvents.ChatWsMessageSendSuccess);
            telemetry.timing(ETelemetryEvents.ChatWsMessageSendTime, Date.now() - clientEvent.timestamp);
          }

          clientEvents.set(uid, { ...clientEvent, status: ERequestApiStatus.Succeed });

          dispatch(setSendMessageSuccess({ message, messageId: uid, messagesMap }));

          // Если была отправка сообщения с файлами (фото, документ, фотофайл)
          if (type === 'textAndFiles') {
            const fileUid = images[0]?.uid || documents?.[0]?.uid || imagesAsDocuments?.[0]?.uid;
            const fileType = fileTypes[0] as TFileTypes;

            dispatch(setWsSendSucceed({ chatId: chatId as string, uid }));
            trackFileSend({
              fileType,
              chatId: chatId as string,
              status: 'success',
              fileId: fileUid,
              opponentId: opponent?.userId,
            });
          }

          if (currentRoute === ERoute.Archive && chatId) {
            dispatch(removeFromArchive(chatId));
            trackUnarchivedButtonClick(opponent?.userId);
          }

          break;
        }
        case 'status':
          if (data.status) {
            dispatch(setUserStatus(data.status));
            telemetry.increment(ETelemetryEvents.ChatWsUserStatusSuccess);
          }
          break;
        case 'unread':
          dispatch(setUnreadMessagesCount(data.unread));

          break;
        case 'errorWithModal': {
          const { modalMessage } = data as IWsModalErrorEvent;

          const exception = new UnknownError({
            domain: EExceptionDomain.Ws,
            message: modalMessage.title || 'Получение модалки с ошибкой по ws',
          });

          logger.warning(exception);
          // TODO: собрать кейсы, когда приходят события ошибки без uid'ов
          // В этом случае скорее всего нужно выкидывать исключение
          if (data.uid) {
            dispatch(setWsModalError(data as IWsModalErrorEvent));
          }
          break;
        }
        case 'error': {
          const { error } = data as IWsErrorEvent;

          const exception = new UnknownError({
            domain: EExceptionDomain.Ws,
            message: error?.message || 'Получение ошибки по ws',
          });

          logger.warning(exception);
          // TODO: собрать кейсы, когда приходят события ошибки без uid'ов
          // В этом случае скорее всего нужно выкидывать исключение
          if (data.uid) {
            dispatch(setWsError(data as IWsErrorEvent));
          }
        }
      }
    });

    const onCloseId = eventChannel.addEventChannelListener('close', event => {
      eventChannel.removeIncommingMessageListener(onMessageId as string);
      eventChannel.removeEventChannelListener('close', onCloseId);

      telemetry.increment(ETelemetryEvents.ChatWsSocketClose);

      /**
       * @description
       * Пробуем переоткрыть соединение, если вкладка активна и это не разрыв на уровне приложения (code >= 4000).
       * Если появятся кастомные коды, которые потребуют переподключения, нужно будет изменить условие.
       */
      if (event.code < 4000 && document.hasFocus()) {
        dispatch(openEventChannel(true));
      }
    });
  };
